import React, { useState } from 'react';
import {
    Box,
    Typography,
    Button,
    TextField,
    Container,
    Paper,
    Dialog,
    DialogContent,
    DialogActions,
    Alert
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { useNavigate } from 'react-router-dom';
import { AuthService } from '@df/api';
import { getSafeApiErrorMessage, isNetworkError } from '../../utils/errorUtils';

const ResendVerification = () => {
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [modalStatus, setModalStatus] = useState(null); // 'success' | 'error'
    const [modalMessage, setModalMessage] = useState('');
    const [fieldErrors, setFieldErrors] = useState({});

    const handleEmailChange = (event) => {
        setEmail(event.target.value);
        
        // Clear field errors when user starts typing
        if (fieldErrors.email) {
            setFieldErrors(prev => ({
                ...prev,
                email: null
            }));
        }
    };

    const validateEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const handleResendVerification = async () => {
        // Clear previous errors
        setFieldErrors({});

        // Validate email
        if (!email) {
            setFieldErrors({ email: 'Email address is required' });
            return;
        }

        if (!validateEmail(email)) {
            setFieldErrors({ email: 'Please enter a valid email address' });
            return;
        }

        setLoading(true);
        
        try {
            const response = await AuthService.resendVerification(email);
            
            setModalStatus('success');
            setModalMessage('Verification email sent successfully! Please check your inbox and click the verification link.');
            setModalOpen(true);
            
            // Clear the email field
            setEmail('');
            
        } catch (error) {
            console.error('Resend verification error:', error);
            setModalStatus('error');
            if (isNetworkError(error)) {
                setModalMessage('Server unreachable. Is the backend running?');
            } else {
                setModalMessage(getSafeApiErrorMessage(error, 'Failed to send verification email. Please try again.'));
            }
            setModalOpen(true);
        } finally {
            setLoading(false);
        }
    };

    const handleCloseModal = () => {
        setModalOpen(false);
        if (modalStatus === 'success') {
            navigate('/login');
        }
    };

    const handleBackToLogin = () => {
        navigate('/login');
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                py: 4
            }}
        >
            <Container maxWidth="sm">
                <Paper
                    elevation={10}
                    sx={{
                        p: 4,
                        borderRadius: 3,
                        background: 'rgba(255, 255, 255, 0.95)',
                        backdropFilter: 'blur(10px)'
                    }}
                >
                    {/* Header */}
                    <Box textAlign="center" mb={4}>
                        <Typography variant="h4" component="h1" gutterBottom sx={{ color: '#2c5aa0', fontWeight: 'bold' }}>
                            Resend Verification Email
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            Enter your email address to receive a new verification link
                        </Typography>
                    </Box>

                    {/* Email Field */}
                    <TextField
                        type="email"
                        label="Email Address"
                        fullWidth
                        value={email}
                        onChange={handleEmailChange}
                        error={!!fieldErrors.email}
                        helperText={fieldErrors.email}
                        sx={{ mb: 3 }}
                    />

                    {/* Submit Button */}
                    <Button
                        variant="contained"
                        fullWidth
                        endIcon={<ArrowForwardIcon />}
                        onClick={handleResendVerification}
                        disabled={!email || loading}
                        sx={{
                            py: 1.5,
                            backgroundColor: '#2D5EFF',
                            '&:hover': {
                                backgroundColor: '#1e4bd8'
                            },
                            mb: 2
                        }}
                    >
                        {loading ? 'Sending...' : 'Resend Verification Email'}
                    </Button>

                    {/* Back to Login Button */}
                    <Button
                        variant="outlined"
                        fullWidth
                        startIcon={<ArrowBackIcon />}
                        onClick={handleBackToLogin}
                        disabled={loading}
                        sx={{
                            py: 1.5,
                            borderColor: '#2D5EFF',
                            color: '#2D5EFF',
                            '&:hover': {
                                borderColor: '#1e4bd8',
                                backgroundColor: 'rgba(45, 94, 255, 0.04)'
                            }
                        }}
                    >
                        Back to Login
                    </Button>

                    {/* Success/Error Modal */}
                    <Dialog open={modalOpen} onClose={handleCloseModal} maxWidth="sm" fullWidth>
                        <DialogContent sx={{ pt: 3 }}>
                            <Box textAlign="center">
                                {modalStatus === 'success' ? (
                                    <CheckCircleIcon sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
                                ) : (
                                    <ErrorIcon sx={{ fontSize: 60, color: 'error.main', mb: 2 }} />
                                )}
                                <Typography variant="h6" gutterBottom>
                                    {modalStatus === 'success' ? 'Email Sent!' : 'Error'}
                                </Typography>
                                <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                                    {modalMessage}
                                </Typography>
                                {modalStatus === 'success' && (
                                    <Alert severity="info" sx={{ textAlign: 'left' }}>
                                        <Typography variant="body2">
                                            <strong>Next Steps:</strong><br />
                                            1. Check your email inbox<br />
                                            2. Look for the verification email<br />
                                            3. Click the verification link<br />
                                            4. Return to login with your account
                                        </Typography>
                                    </Alert>
                                )}
                            </Box>
                        </DialogContent>
                        <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
                            <Button
                                onClick={handleCloseModal}
                                variant="contained"
                                sx={{
                                    backgroundColor: modalStatus === 'success' ? '#2D5EFF' : '#f44336',
                                    '&:hover': {
                                        backgroundColor: modalStatus === 'success' ? '#1e4bd8' : '#d32f2f'
                                    }
                                }}
                            >
                                {modalStatus === 'success' ? 'Continue to Login' : 'Try Again'}
                            </Button>
                        </DialogActions>
                    </Dialog>
                </Paper>
            </Container>
        </Box>
    );
};

export default ResendVerification;
